import type { ValueGetterParams, ValueSetterParams } from 'ag-grid-community';
import type { TFunction } from 'i18next';
import type { BombiixColumnDef, BombiixColumnDefOrGroup } from '../../../../../../Components/BombiixAGGrid/types';
import { ImageCell, GroupHeader } from '../../../../../../Components/BombiixAGGrid';
import type { MeasurementGridRow } from './grid-types';
import SizeCell from './cells/SizeCell';
import ProductExpandCell from './cells/ProductExpandCell';
import ProductNumberCell from './cells/ProductNumberCell';
import DocActionsCell from './cells/DocActionsCell';
import SelectAllHeader from './cells/SelectAllHeader';
import ExpandAllHeader from './cells/ExpandAllHeader';
import PushHeaderAction from './cells/PushHeaderAction';
import { SIZE_FIELD_PREFIX, sizeCellValue, sizeFillKind } from './measurement-fill';

/** Handlers for the supporting-document action icons, injected via the actions column's cellProps. */
export interface DocActionHandlers {
  onUpload: (row: MeasurementGridRow) => void;
  onPreview: (row: MeasurementGridRow) => void;
  onDownload: (row: MeasurementGridRow) => void;
  onDelete: (row: MeasurementGridRow) => void;
}

/** Pinned columns sit in the leaf-header row only — empty padded cell above (Figma two-tier header). */
const PINNED_HEADER = { suppressSpanHeaderHeight: true } as const;

/** Product-level cells show their value only on the product's first row (rowspan effect). */
function firstRowValue(field: keyof MeasurementGridRow) {
  return (params: ValueGetterParams<MeasurementGridRow>): string => {
    const row = params.data;
    if (!row || !row.isFirstOfProduct) return '';
    return String(row[field] ?? '');
  };
}

/** Document cells are blank on rows without a document (the shorter side of the zip). */
function docRowValue(field: keyof MeasurementGridRow) {
  return (params: ValueGetterParams<MeasurementGridRow>): string => {
    const row = params.data;
    if (!row || !row.hasDoc) return '';
    return String(row[field] ?? '');
  };
}

/** Spec (POM) input cells are read-only on rows without a POM item (the shorter side of the zip). */
const isNotPomRow = (row: MeasurementGridRow): boolean => !row.hasPom;

/**
 * Columns for the multi-product spec grid, matching the Figma "Multi Spec Table":
 * leading pinned columns (select · expand · image · Product Number), then the collapsible
 * "Spec Table" and "Supporting Documents" column groups (open by default). In each group
 * the first column stays visible when collapsed; the rest are `columnGroupShow: 'open'`.
 */
/** Build a cell-class resolver that stacks the amber (changed) class with a red required-field class. */
function codeDescClass(field: 'code' | 'description', changedFlag: keyof MeasurementGridRow, invalidCells: Set<string>) {
  return (row: MeasurementGridRow): string[] | undefined => {
    const classes: string[] = [];
    if (row[changedFlag]) classes.push('bg-amber');
    if (invalidCells.has(`${row.devOrderId}::${row.rowKey}::${field}`)) classes.push('cell-invalid');
    return classes.length ? classes : undefined;
  };
}

export function buildColumnDefs(
  sizeRanges: string[],
  t: TFunction,
  docActions: DocActionHandlers,
  invalidCells: Set<string>,
): BombiixColumnDefOrGroup<MeasurementGridRow>[] {
  const sizeColumns: BombiixColumnDef<MeasurementGridRow>[] = sizeRanges.map((size) => ({
    headerName: size,
    colId: `size-${size}`,
    /* The field, getter, setter and read-only rule exist for the fill handle: the setter writes a
       dropped value onto `row[field]`, the getter reads it back, and only cells the rule allows are
       filled. The setter writes the key whole: left to AG Grid, a size name with a dot (7.5) would be
       read as a nested path and the value lost. The cell itself keeps rendering from `row.gradings`;
       `equals` keeps it repainting on every row update. */
    field: `${SIZE_FIELD_PREFIX}${size}` as NonNullable<BombiixColumnDef<MeasurementGridRow>['field']>,
    fillable: true,
    valueGetter: (params: ValueGetterParams<MeasurementGridRow>) => sizeCellValue(params.data, size),
    valueSetter: (params: ValueSetterParams<MeasurementGridRow>) => {
      (params.data as Record<string, unknown>)[`${SIZE_FIELD_PREFIX}${size}`] = params.newValue;
      return true;
    },
    equals: () => false,
    getCellReadOnly: (row: MeasurementGridRow) => sizeFillKind(row, size) === null,
    cellRenderer: SizeCell,
    cellProps: { sizeName: size },
    width: 100,
    minWidth: 100,
    sortable: false,
    headerClass: 'cag-header-center',
    columnGroupShow: 'open',
  }));

  return [
    {
      headerName: '',
      colId: 'select',
      field: 'selected',
      cellType: 'checkbox',
      fillable: false,
      getCellHidden: isNotPomRow,
      /* The push interaction gates which checkboxes may be changed, and colours the source vs targets. */
      getCellDisabled: (row) => row.selectDisabled,
      getCellClass: (row) => (row.selectRole === 'target' ? 'is-push-target' : undefined),
      headerComponent: SelectAllHeader,
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      pinned: 'left',
      sortable: false,
      resizable: false,
      ...PINNED_HEADER,
    },
    {
      headerName: '',
      colId: 'expand',
      cellRenderer: ProductExpandCell,
      headerComponent: ExpandAllHeader,
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      pinned: 'left',
      sortable: false,
      resizable: false,
      ...PINNED_HEADER,
    },
    {
      headerName: t('MEASUREMENTS_MODULE.COL_IMAGE'),
      colId: 'image',
      field: 'image',
      cellRenderer: ImageCell,
      getCellHidden: (row) => !row.isFirstOfProduct,
      width: 59,
      minWidth: 59,
      maxWidth: 59,
      pinned: 'left',
      sortable: false,
      resizable: false,
      suppressHeaderMenuButton: true,
      ...PINNED_HEADER,
    },
    {
      headerName: t('MEASUREMENTS_MODULE.COL_PRODUCT_NUMBER'),
      colId: 'number',
      cellRenderer: ProductNumberCell,
      valueGetter: firstRowValue('number'),
      width: 150,
      minWidth: 140,
      pinned: 'left',
      ...PINNED_HEADER,
    },
    {
      headerName: t('MEASUREMENTS_MODULE.GROUP_SPEC_TABLE'),
      groupId: 'specTable',
      marryChildren: true,
      openByDefault: true,
      headerGroupComponent: GroupHeader,
      headerGroupComponentParams: { actionsComponent: PushHeaderAction },
      headerClass: 'cag-group-accent-blue',
      children: [
        { headerName: t('MEASUREMENTS_MODULE.COL_GRADING_GROUP'), colId: 'gradingGroup', valueGetter: firstRowValue('gradingGroupName'), width: 150, minWidth: 150 },
        {
          headerName: t('MEASUREMENTS_MODULE.COL_POM_CODE'),
          colId: 'code',
          field: 'code',
          cellType: 'text',
          getCellReadOnly: isNotPomRow,
          getCellClass: codeDescClass('code', 'isUserCodeChanged', invalidCells),
          width: 150,
          minWidth: 150,
          columnGroupShow: 'open',
        },
        {
          headerName: t('MEASUREMENTS_MODULE.COL_POM_DESCRIPTION'),
          colId: 'description',
          field: 'description',
          cellType: 'text',
          getCellReadOnly: isNotPomRow,
          getCellClass: codeDescClass('description', 'isUserDescChanged', invalidCells),
          width: 240,
          minWidth: 240,
          columnGroupShow: 'open',
        },
        ...sizeColumns,
        {
          headerName: t('MEASUREMENTS_MODULE.COL_TOLERANCE'),
          colId: 'tolerance',
          field: 'tolMinus',
          cellType: 'text',
          getCellReadOnly: isNotPomRow,
          width: 100,
          minWidth: 100,
          sortable: false,
          columnGroupShow: 'open',
        },
      ],
    },
    {
      headerName: t('MEASUREMENTS_MODULE.GROUP_SUPPORTING_DOCS'),
      groupId: 'supportingDocs',
      marryChildren: true,
      openByDefault: true,
      headerGroupComponent: GroupHeader,
      headerClass: 'cag-group-accent-green',
      children: [
        { headerName: t('MEASUREMENTS_MODULE.COL_DOCUMENT_NAME'), colId: 'documentName', valueGetter: docRowValue('documentName'), minWidth: 200, flex: 1 },
        { headerName: t('MEASUREMENTS_MODULE.COL_DOCUMENT_TYPE'), colId: 'documentType', valueGetter: docRowValue('documentType'), width: 150, minWidth: 150, columnGroupShow: 'open' },
        /* Wide enough for all five actions — preview, download, info, delete and upload — at 20px plus
           4px gaps inside the grid's 12px cell padding. Too narrow and the last one (upload) is clipped. */
        { headerName: '', colId: 'docActions', cellRenderer: DocActionsCell, cellProps: { ...docActions }, width: 150, minWidth: 150, sortable: false, columnGroupShow: 'open' },
      ],
    },
  ];
}
